import React, { useState, useEffect } from 'react';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  BarElement, 
  ArcElement,
  Title, 
  Tooltip, 
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar, Doughnut, Pie } from 'react-chartjs-2';
import { 
  CalendarIcon, 
  CurrencyDollarIcon, 
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { reservationService } from '../firebase/services';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const AnalyticsDashboard = () => {
  const [analyticsData, setAnalyticsData] = useState({
    reservations: { 
      summary: { total: 0, confirmed: 0, cancelled: 0, pending: 0 },
      trend: { labels: [], datasets: [] },
      byStatus: { labels: [], datasets: [] },
      bySpace: { labels: [], datasets: [] }
    },
    revenue: { 
      total: 0, 
      average: 0, 
      growth: 0,
      trend: { labels: [], datasets: [] }
    },
    spaces: {
      utilization: { labels: [], datasets: [] }
    }
  });
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('30');

  useEffect(() => {
    loadAnalyticsData();
  }, [selectedPeriod]);

  const loadAnalyticsData = async () => {
    setLoading(true);
    try {
      const reservations = await reservationService.getAllReservations();
      const processedData = processAnalyticsData(reservations, selectedPeriod);
      setAnalyticsData(processedData);
    } catch (error) {
      console.error('Error loading analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const processAnalyticsData = (reservations, period) => {
    const days = parseInt(period);
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const filteredReservations = reservations.filter(reservation => {
      const reservationDate = new Date(reservation.createTime?.toDate?.() || reservation.createTime);
      return reservationDate >= startDate && reservationDate <= endDate;
    });

    // Generate date labels
    const dateLabels = [];
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      dateLabels.push(date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
    }

    // Process reservations data
    const reservationsByDate = {};
    const reservationsByStatus = {};
    const reservationsBySpace = {};

    filteredReservations.forEach(reservation => {
      const date = new Date(reservation.createTime?.toDate?.() || reservation.createTime);
      const dateKey = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      
      // Count by date
      reservationsByDate[dateKey] = (reservationsByDate[dateKey] || 0) + 1;
      
      // Count by status
      const status = reservation.state || 'unknown';
      reservationsByStatus[status] = (reservationsByStatus[status] || 0) + 1;
      
      // Count by space
      const spaceId = reservation.spaceId || 'unknown';
      reservationsBySpace[spaceId] = (reservationsBySpace[spaceId] || 0) + 1;
    });

    // Calculate revenue data
    const revenueByDate = {};
    const totalRevenue = filteredReservations.reduce((sum, reservation) => {
      const date = new Date(reservation.createTime?.toDate?.() || reservation.createTime);
      const dateKey = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const amount = reservation.price || 0;
      
      revenueByDate[dateKey] = (revenueByDate[dateKey] || 0) + amount;
      return sum + amount;
    }, 0);

    // Generate datasets for charts
    const reservationTrendData = dateLabels.map(label => reservationsByDate[label] || 0);
    const revenueTrendData = dateLabels.map(label => revenueByDate[label] || 0);

    return {
      reservations: {
        trend: {
          labels: dateLabels,
          datasets: [{
            label: 'Daily Reservations',
            data: reservationTrendData,
            borderColor: 'rgb(59, 130, 246)',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            fill: true,
            tension: 0.4
          }]
        },
        byStatus: {
          labels: Object.keys(reservationsByStatus),
          datasets: [{
            data: Object.values(reservationsByStatus),
            backgroundColor: [
              'rgba(34, 197, 94, 0.8)',
              'rgba(59, 130, 246, 0.8)',
              'rgba(239, 68, 68, 0.8)',
              'rgba(156, 163, 175, 0.8)'
            ],
            borderColor: [
              'rgb(34, 197, 94)',
              'rgb(59, 130, 246)',
              'rgb(239, 68, 68)',
              'rgb(156, 163, 175)'
            ],
            borderWidth: 2
          }]
        },
        bySpace: {
          labels: Object.keys(reservationsBySpace).slice(0, 10), // Top 10 spaces
          datasets: [{
            label: 'Reservations by Space',
            data: Object.values(reservationsBySpace).slice(0, 10),
            backgroundColor: 'rgba(99, 102, 241, 0.8)',
            borderColor: 'rgb(99, 102, 241)',
            borderWidth: 1
          }]
        },
        summary: {
          total: filteredReservations.length,
          confirmed: reservationsByStatus.confirmed || 0,
          cancelled: reservationsByStatus.cancelled || 0,
          pending: reservationsByStatus.pending || 0
        }
      },
      revenue: {
        trend: {
          labels: dateLabels,
          datasets: [{
            label: 'Daily Revenue (₹)',
            data: revenueTrendData,
            borderColor: 'rgb(34, 197, 94)',
            backgroundColor: 'rgba(34, 197, 94, 0.1)',
            fill: true,
            tension: 0.4
          }]
        },
        total: totalRevenue,
        average: totalRevenue / days,
        growth: calculateGrowthRate(revenueTrendData)
      },
      spaces: {
        utilization: {
          labels: ['Occupied', 'Available', 'Disabled'],
          datasets: [{
            data: [70, 20, 10], // Mock data - replace with actual space utilization
            backgroundColor: [
              'rgba(239, 68, 68, 0.8)',
              'rgba(34, 197, 94, 0.8)',
              'rgba(156, 163, 175, 0.8)'
            ],
            borderColor: [
              'rgb(239, 68, 68)',
              'rgb(34, 197, 94)',
              'rgb(156, 163, 175)'
            ],
            borderWidth: 2
          }]
        }
      }
    };
  };

  const calculateGrowthRate = (data) => {
    if (data.length < 2) return 0;
    const firstHalf = data.slice(0, Math.floor(data.length / 2));
    const secondHalf = data.slice(Math.floor(data.length / 2));
    
    const firstAvg = firstHalf.reduce((sum, val) => sum + val, 0) / firstHalf.length;
    const secondAvg = secondHalf.reduce((sum, val) => sum + val, 0) / secondHalf.length;
    
    if (firstAvg === 0) return secondAvg > 0 ? 100 : 0;
    return ((secondAvg - firstAvg) / firstAvg) * 100;
  };

  const StatCard = ({ title, value, change, icon: Icon, color }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {change !== undefined && (
            <div className={`flex items-center mt-2 ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {change >= 0 ? (
                <ArrowTrendingUpIcon className="w-4 h-4 mr-1" />
              ) : (
                <ArrowTrendingDownIcon className="w-4 h-4 mr-1" />
              )}
              <span className="text-sm font-medium">
                {Math.abs(change).toFixed(1)}%
              </span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );

  const ChartCard = ({ title, children, className = "" }) => (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 ${className}`}>
      <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>
      {children}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-600">Comprehensive insights into reservations, spaces, and revenue</p>
        </div>
        
        {/* Period Selector */}
        <div className="flex items-center space-x-4 mt-4 sm:mt-0">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last year</option>
          </select>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Reservations"
          value={analyticsData.reservations.summary.total}
          change={analyticsData.revenue.growth}
          icon={CalendarIcon}
          color="bg-blue-500"
        />
        <StatCard
          title="Total Revenue"
          value={`₹${analyticsData.revenue.total.toLocaleString()}`}
          change={analyticsData.revenue.growth}
          icon={CurrencyDollarIcon}
          color="bg-green-500"
        />
        <StatCard
          title="Average Daily Revenue"
          value={`₹${analyticsData.revenue.average.toFixed(0)}`}
          icon={ArrowTrendingUpIcon}
          color="bg-purple-500"
        />
        <StatCard
          title="Confirmation Rate"
          value={`${((analyticsData.reservations.summary.confirmed / analyticsData.reservations.summary.total) * 100).toFixed(1)}%`}
          icon={CheckCircleIcon}
          color="bg-indigo-500"
        />
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Reservation Trends */}
        <ChartCard title="Reservation Trends">
          <Line
            data={analyticsData.reservations.trend}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: 'top',
                },
                title: {
                  display: false,
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                },
              },
            }}
          />
        </ChartCard>

        {/* Revenue Trends */}
        <ChartCard title="Revenue Trends">
          <Line
            data={analyticsData.revenue.trend}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: 'top',
                },
                title: {
                  display: false,
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                  ticks: {
                    callback: function(value) {
                      return '₹' + value.toLocaleString();
                    }
                  }
                },
              },
            }}
          />
        </ChartCard>

        {/* Reservation Status Distribution */}
        <ChartCard title="Reservation Status Distribution">
          <Doughnut
            data={analyticsData.reservations.byStatus}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: 'bottom',
                },
              },
            }}
          />
        </ChartCard>

        {/* Space Utilization */}
        <ChartCard title="Space Utilization">
          <Pie
            data={analyticsData.spaces.utilization}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: 'bottom',
                },
              },
            }}
          />
        </ChartCard>

        {/* Top Spaces by Reservations */}
        <ChartCard title="Top Spaces by Reservations" className="lg:col-span-2">
          <Bar
            data={analyticsData.reservations.bySpace}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  display: false,
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                },
              },
            }}
          />
        </ChartCard>
      </div>

      {/* Detailed Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ChartCard title="Reservation Insights">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Confirmed</span>
              <span className="font-semibold text-green-600">
                {analyticsData.reservations.summary.confirmed}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Cancelled</span>
              <span className="font-semibold text-red-600">
                {analyticsData.reservations.summary.cancelled}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Pending</span>
              <span className="font-semibold text-yellow-600">
                {analyticsData.reservations.summary.pending}
              </span>
            </div>
          </div>
        </ChartCard>

        <ChartCard title="Revenue Insights">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Total Revenue</span>
              <span className="font-semibold text-green-600">
                ₹{analyticsData.revenue.total.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Daily Average</span>
              <span className="font-semibold text-blue-600">
                ₹{analyticsData.revenue.average.toFixed(0)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Growth Rate</span>
              <span className={`font-semibold ${analyticsData.revenue.growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {analyticsData.revenue.growth >= 0 ? '+' : ''}{analyticsData.revenue.growth.toFixed(1)}%
              </span>
            </div>
          </div>
        </ChartCard>

        <ChartCard title="Performance Metrics">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Occupancy Rate</span>
              <span className="font-semibold text-indigo-600">70%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Avg. Booking Value</span>
              <span className="font-semibold text-purple-600">
                ₹{(analyticsData.revenue.total / analyticsData.reservations.summary.total).toFixed(0)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Peak Hours</span>
              <span className="font-semibold text-orange-600">9 AM - 6 PM</span>
            </div>
          </div>
        </ChartCard>
      </div>
    </div>
  );
};

export default AnalyticsDashboard; 